import React, { useEffect, useRef, useState } from "react";
import {
  MapPin,
  Pencil,
  Trash2,
  Plus,
  Phone,
  Mail,
  Home,
  Star,
} from "lucide-react";
import { apiClient } from "../../services/apiClientService";
import { isValidEmail } from "../../utils/validators";

const emptyAddress = {
  fullName: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  state: "",
  country: "India",
  isPrimary: false,
};

const inputClass =
  "w-full rounded-xl border border-stone-200 bg-white px-3.5 py-2.5 text-sm text-stone-900 shadow-sm outline-none transition placeholder:text-stone-400 focus:border-orange-300 focus:ring-2 focus:ring-orange-200/60";

const AddressSection = () => {
  const [addresses, setAddresses] = useState([]);
  const [form, setForm] = useState(emptyAddress);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const formRef = useRef(null);

  const fetchAddresses = async () => {
    const res = await apiClient("GET", "/api/users/shipping-addresses");
    if (res?.addresses) setAddresses(res.addresses);
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  const handleDelete = async (id) => {
    const ok = window.confirm(
      "Remove this address from your account? You can add it again later."
    );
    if (!ok) return;

    try {
      const res = await apiClient(
        "DELETE",
        `/api/users/shipping-addresses/${id}`
      );
      if (res?.addresses) setAddresses(res.addresses);
    } catch (e) {
      alert(e.message || "Could not delete address.");
    }
  };

  const handleEdit = (address) => {
    setForm({ ...emptyAddress, ...address });
    setEditingId(address._id);
    setShowForm(true);

    setTimeout(() => {
      if (!formRef.current) return;
      const topOffset =
        formRef.current.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: topOffset, behavior: "smooth" });
    }, 100);
  };

  const handleSave = async () => {
    if (form.email?.trim() && !isValidEmail(form.email)) {
      alert("Please enter a valid email address.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const res = await apiClient(
          "PATCH",
          `/api/users/shipping-addresses/${editingId}`,
          form
        );
        if (res?.addresses) setAddresses(res.addresses);
      } else {
        const res = await apiClient(
          "POST",
          "/api/users/shipping-addresses",
          form
        );
        if (res?.addresses) setAddresses(res.addresses);
      }

      setForm(emptyAddress);
      setEditingId(null);
      setShowForm(false);

      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    } catch (e) {
      alert(e.message || "Could not save address.");
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSetPrimary = async (addressId) => {
    try {
      const res = await apiClient("PATCH", `/api/users/shipping-addresses/${addressId}`, {
        isPrimary: true,
      });
      if (res?.addresses) setAddresses(res.addresses);
    } catch (e) {
      alert(e.message || "Could not update primary address.");
    }
  };

  const openAddForm = () => {
    setShowForm(true);
    setForm(emptyAddress);
    setEditingId(null);
    setTimeout(() => {
      if (!formRef.current) return;
      const topOffset =
        formRef.current.getBoundingClientRect().top +
        window.pageYOffset -
        80;
      window.scrollTo({ top: topOffset, behavior: "smooth" });
    }, 100);
  };

  return (
    <div className="mx-auto max-w-2xl rounded-3xl border border-stone-200/80 bg-gradient-to-b from-orange-50/90 via-amber-50/40 to-stone-50 p-5 shadow-lg shadow-stone-200/50 sm:p-8">
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-stone-900 sm:text-2xl">
            Saved addresses
          </h2>
          <p className="mt-1 text-sm text-stone-600">
            Your <strong>primary</strong> address is pre-filled at checkout. Edit or add anytime.
          </p>
        </div>
        <button
          type="button"
          onClick={openAddForm}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:from-orange-600 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2"
        >
          <Plus className="h-4 w-4" aria-hidden />
          Add address
        </button>
      </div>

      {addresses.length === 0 && !showForm ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-stone-300 bg-white/60 py-14 text-center">
          <span className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-orange-700 ring-1 ring-orange-200/80">
            <MapPin className="h-7 w-7" aria-hidden />
          </span>
          <p className="text-sm font-medium text-stone-800">No addresses yet</p>
          <p className="mt-1 max-w-xs text-sm text-stone-500">
            Add a delivery address so checkout is faster next time.
          </p>
          <button
            type="button"
            onClick={openAddForm}
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-orange-700 underline decoration-orange-300 underline-offset-4 hover:text-orange-900"
          >
            <Plus className="h-4 w-4" />
            Add your first address
          </button>
        </div>
      ) : (
        <ul className="space-y-3">
          {addresses.map((addr) => (
            <li key={addr._id}>
              <article className="group flex flex-col gap-4 rounded-2xl border border-stone-200 bg-white/85 p-4 shadow-sm transition hover:border-orange-200/80 hover:bg-orange-50/30 hover:shadow-md sm:flex-row sm:items-stretch sm:justify-between sm:gap-5 sm:p-5">
                <div className="flex min-w-0 flex-1 gap-3">
                  <span
                    className="mt-0.5 hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-700 ring-1 ring-orange-100 sm:inline-flex"
                    aria-hidden
                  >
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 font-semibold text-stone-900">
                      {addr.fullName}
                      {addr.isPrimary ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-900 ring-1 ring-orange-200/80">
                          <Star className="h-3 w-3 fill-orange-500 text-orange-600" aria-hidden />
                          Primary
                        </span>
                      ) : null}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-stone-600">
                      {addr.address}
                      <br />
                      <span className="text-stone-500">
                        {[addr.city, addr.state, addr.country]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </p>
                    <div className="mt-3 flex flex-col gap-1.5 text-sm text-stone-600 sm:flex-row sm:flex-wrap sm:gap-x-4">
                      {addr.phone ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Phone
                            className="h-3.5 w-3.5 shrink-0 text-orange-600/80"
                            aria-hidden
                          />
                          <span className="truncate">{addr.phone}</span>
                        </span>
                      ) : null}
                      {addr.email ? (
                        <span className="inline-flex min-w-0 items-center gap-1.5">
                          <Mail
                            className="h-3.5 w-3.5 shrink-0 text-orange-600/80"
                            aria-hidden
                          />
                          <span className="truncate">{addr.email}</span>
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-stone-100 pt-3 sm:flex-nowrap sm:border-t-0 sm:border-l sm:pl-4 sm:pt-0">
                  {!addr.isPrimary ? (
                    <button
                      type="button"
                      onClick={() => handleSetPrimary(addr._id)}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2 text-sm font-medium text-amber-900 shadow-sm transition hover:bg-amber-100 sm:flex-none"
                    >
                      <Star className="h-3.5 w-3.5" aria-hidden />
                      Set primary
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => handleEdit(addr)}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 shadow-sm transition hover:border-orange-200 hover:bg-orange-50/80 hover:text-orange-900 sm:flex-none"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(addr._id)}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-rose-200/80 bg-white px-3 py-2 text-sm font-medium text-rose-700 shadow-sm transition hover:bg-rose-50 sm:flex-none"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    Delete
                  </button>
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <div
          ref={formRef}
          className="mt-6 rounded-2xl border border-stone-200 bg-white/95 p-5 shadow-inner sm:p-6"
        >
          <div className="mb-4 flex items-center gap-2 border-b border-stone-100 pb-4">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-amber-800">
              <Home className="h-4 w-4" aria-hidden />
            </span>
            <div>
              <h3 className="text-base font-semibold text-stone-900">
                {editingId ? "Edit address" : "New address"}
              </h3>
              <p className="text-xs text-stone-500">
                Use the same details you give to couriers for smooth delivery.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
            <label className="block md:col-span-1">
              <span className="mb-1 block text-xs font-medium text-stone-600">
                Full name
              </span>
              <input
                placeholder="As on parcel"
                value={form.fullName}
                onChange={handleFieldChange("fullName")}
                className={inputClass}
              />
            </label>
            <label className="block md:col-span-1">
              <span className="mb-1 block text-xs font-medium text-stone-600">
                Email
              </span>
              <input
                placeholder="you@example.com"
                type="email"
                value={form.email}
                onChange={handleFieldChange("email")}
                className={inputClass}
              />
            </label>
            <label className="block md:col-span-1">
              <span className="mb-1 block text-xs font-medium text-stone-600">
                Phone
              </span>
              <input
                placeholder="Mobile number"
                value={form.phone}
                onChange={handleFieldChange("phone")}
                className={inputClass}
              />
            </label>
            <label className="block md:col-span-1">
              <span className="mb-1 block text-xs font-medium text-stone-600">
                Country
              </span>
              <input
                placeholder="Country"
                value={form.country}
                onChange={handleFieldChange("country")}
                className={inputClass}
              />
            </label>
            <label className="block md:col-span-1">
              <span className="mb-1 block text-xs font-medium text-stone-600">
                State
              </span>
              <input
                placeholder="State / province"
                value={form.state}
                onChange={handleFieldChange("state")}
                className={inputClass}
              />
            </label>
            <label className="block md:col-span-1">
              <span className="mb-1 block text-xs font-medium text-stone-600">
                City
              </span>
              <input
                placeholder="City"
                value={form.city}
                onChange={handleFieldChange("city")}
                className={inputClass}
              />
            </label>
            <label className="block md:col-span-2">
              <span className="mb-1 block text-xs font-medium text-stone-600">
                Street address
              </span>
              <textarea
                placeholder="House / flat, street, landmark"
                rows={3}
                value={form.address}
                onChange={handleFieldChange("address")}
                className={`${inputClass} resize-y min-h-[88px]`}
              />
            </label>

            <label className="flex cursor-pointer items-start gap-2 md:col-span-2">
              <input
                type="checkbox"
                checked={!!form.isPrimary}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, isPrimary: e.target.checked }))
                }
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-stone-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm leading-snug text-stone-700">
                <span className="font-semibold text-stone-900">Primary address</span> — used as the
                default at checkout unless you choose another.
              </span>
            </label>
          </div>

          <div className="mt-5 flex flex-col-reverse gap-2 border-t border-stone-100 pt-4 sm:flex-row sm:justify-end sm:gap-3">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setForm(emptyAddress);
                setEditingId(null);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="rounded-xl px-4 py-2.5 text-sm font-medium text-stone-600 transition hover:bg-stone-100 hover:text-stone-900"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleSave}
              className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:from-orange-600 hover:to-amber-600 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2"
            >
              {saving ? "Saving…" : editingId ? "Save changes" : "Save address"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressSection;
